import React, { useMemo } from 'react';
import { isValid, parse } from 'date-fns';
import { ButtonV2, DatePickerInput, FormControl, SpacingStack, TextV2, useDisclosure, Box, Flex, InputV2, Toggle } from '@withjoy/joykit';
import { InlineLinkButton } from '@apps/acquisitionFunnels/createWedding/components/InlineLinkButton/InlineLinkButton';
import { InputV2InlineLabel } from '@shared/joykit/packages/core/components/InputV2InlineLabel/InputV2InlineLabel';
import { useTranslation } from '@shared/core';
import { useCreateWeddingFormController } from '@apps/acquisitionFunnels/createWedding/routes/CreateWeddingForm/CreateWeddingForm.controller';
import type { CreateWeddingFormProps } from '@apps/acquisitionFunnels/createWedding/routes/CreateWeddingForm/CreateWeddingForm.controller';
import { wrapWithCreateWeddingFormHealthProvider } from './CreateWeddingForm.Health';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { LocationInputV2Expandable, withFormikLocationInputV2 } from '@shared/components/LocationInputV2';
import type { AdvancedLocationInputV2FormFields } from '@shared/components/LocationInputV2';
import { isValidCoordinates } from '@shared/core/places/location';
import { useRemoteFeatureFlag } from '@shared/core/featureFlags/remoteFeatureFlags';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { FormikProvider } from 'formik';
import { CREATE_WEDDING_CONTAINER_MAX_WIDTH } from '@apps/acquisitionFunnels/createWedding/components/CreateWeddingLayout/CreateWeddingLayout';
import { pxToRem } from '@withjoy/joykit/theme';
import { useLocaleContext } from '@localization/context/LocaleContext';
import { useHeroTextByScenario } from '@apps/acquisitionFunnels/createWedding/hooks/useHeroTextByScenario';
import { ReactComponent as IconBrandLogoNoColor } from '@assets/icons/icon-brand-logo-no-color.svg';

const LocationInputV2ExpandableWithFormik = withFormikLocationInputV2(LocationInputV2Expandable);

export const CreateWeddingForm = wrapWithCreateWeddingFormHealthProvider<CreateWeddingFormProps>(props => {
  const { tq } = useTranslation('createWedding');
  const { formik, user, isLoggedIn, getFieldErrorProps, signOut, blockWeddingDate, setBlockWeddingDate, blockEventVenue, setBlockEventVenue, telemetry } =
    useCreateWeddingFormController({
      ...props,
    });

  const { isOpen, onOpen, onClose } = useDisclosure();

  const isMobile = useIsMobileScreen();
  const { payload } = useRemoteFeatureFlag('venueAddressLocationEnabled');
  const { dateFormat } = useLocaleContext();
  const { createWeddingHeroText } = useHeroTextByScenario();

  const placePredictionTypes = useMemo(() => (Object(payload)?.types as string[]) || [], [payload]);

  const handleOnChangeLocation = useEventCallback((values: AdvancedLocationInputV2FormFields) => {
    if (isValidCoordinates(values) && values.placeId) {
      formik.setFieldValue('eventVenue', values);
      telemetry.inputHandler('eventVenue', 'onChange');
      // Save event location same as venue - https://joylife.slack.com/archives/CDWBWGY69/p1758213644418509?thread_ts=1758213586.836009&cid=CDWBWGY69
      formik.setFieldValue('location', { place: values.formattedAddress, placeId: values.placeId, lat: values.latitude, lng: values.longitude });
    }
  });

  const handleDateInputChange = useEventCallback((dateString: string) => {
    const isSameLength = dateString.replace(/(\s)*/g, '').length === dateFormat.replace(/(\s)*/g, '').length;
    const inputDate = parse(dateString, dateFormat, new Date());
    if (!isSameLength || !isValid(inputDate)) {
      formik.setFieldValue('isDateValid', false);
    }
  });

  return (
    <Flex
      width="100%"
      maxWidth={{ _: '100%', sm2: pxToRem(CREATE_WEDDING_CONTAINER_MAX_WIDTH) }}
      flexDirection="column"
      alignItems="center"
      gap={5}
    >
      <IconBrandLogoNoColor
        aria-hidden="true"
        css={{ width: pxToRem(67), height: pxToRem(42) }}
      />
      <TextV2
        as="h1"
        typographyVariant={'hed6'}
        fontWeight={600}
        marginTop={5}
        textAlign={'center'}
      >
        {createWeddingHeroText}
      </TextV2>
      <FormikProvider value={formik}>
        <Box
          as="form"
          onSubmit={formik.handleSubmit}
          width={'100%'}
          marginTop={5}
        >
          <SpacingStack spacing={6}>
            <FormControl
              onFocus={() => telemetry.inputHandler('ownerFirstName', 'onFocus')}
              {...getFieldErrorProps('ownerFirstName')}
            >
              <InputV2InlineLabel
                label={tq`Your First Name`}
                autoComplete="given-name"
                {...formik.getFieldProps('ownerFirstName')}
                {...getFieldErrorProps('ownerFirstName')}
              />
            </FormControl>
            {/* Hidden field to collect user last name by autofill - type=hidden does not autofill value */}
            <InputV2
              {...formik.getFieldProps('ownerLastName')}
              aria-label={tq`Last Name`}
              autoComplete="family-name"
              tabIndex={-1}
              aria-hidden="true"
              overrides={{
                Root: { props: { borderWidth: 0, marginTop: '0px !important', _after: { display: 'none' } } },
                Input: { props: { position: 'absolute', left: '-9999px', height: '1px', width: '1px', opacity: 0 } },
              }}
            />
            <FormControl
              onFocus={() => telemetry.inputHandler('partnerFirstName', 'onFocus')}
              {...getFieldErrorProps('partnerFirstName')}
            >
              <InputV2InlineLabel
                label={tq`Your Partner's First Name`}
                {...formik.getFieldProps('partnerFirstName')}
                {...getFieldErrorProps('partnerFirstName')}
              />
            </FormControl>
            <SpacingStack spacing={5}>
              <LocationInputV2ExpandableWithFormik
                name="eventVenue"
                label={tq`Event Venue`}
                placeholders={[tq`San Francisco City Hall`]}
                placePredictionTypes={placePredictionTypes}
                initialValues={{ placeId: formik.values.eventVenue?.placeId || '', locationValue: formik.values.eventVenue?.locationValue || '' }}
                intent={'border'}
                allowCustomPlace={false}
                shouldCloseOnSelect
                hasPopOver={!isMobile}
                isHideListItemIcon={true}
                onChangeLocation={handleOnChangeLocation}
                shape="square"
                isDisabled={blockEventVenue}
                errorProps={{ textAlign: 'left' }}
                inputVariant="inputV2InlineLabel"
                maxDropdownHeight={pxToRem(370)}
                noOptionsMessage={tq`We didn't find any venues. Try searching for a specific venue name.`}
              />
              <Toggle
                alignIndicator="left"
                id="eventVenue"
                label={
                  <TextV2
                    typographyVariant={'label2'}
                    color={'mono12'}
                  >{tq`We’re still deciding`}</TextV2>
                }
                checked={blockEventVenue}
                onChange={e => {
                  setBlockEventVenue(e.target.checked);
                  telemetry.inputHandler('eventVenue', 'onChange');
                }}
              />
            </SpacingStack>
            <SpacingStack spacing={5}>
              <FormControl {...getFieldErrorProps('weddingDate')}>
                <DatePickerInput
                  isDisabled={blockWeddingDate}
                  endElement={null}
                  mask={null}
                  minDate={new Date()}
                  dateFormat={dateFormat}
                  placeholder=""
                  {...formik.getFieldProps('weddingDate')}
                  onFocus={() => telemetry.inputHandler('eventDate', 'onFocus')}
                  onOpen={onOpen}
                  onClose={onClose}
                  isOpen={isOpen}
                  onChange={date => {
                    formik.setFieldValue('isDateValid', true);
                    formik.setFieldValue('weddingDate', date);
                    telemetry.inputHandler('eventDate', 'onChange');
                  }}
                  onInputChange={handleDateInputChange}
                  enableCloseOnSelect={true}
                  label={tq`Event Date`}
                  inputVariant="inputV2InlineLabel"
                />
              </FormControl>
              <Toggle
                alignIndicator="left"
                id="eventDate"
                label={
                  <TextV2
                    typographyVariant={'label2'}
                    color={'mono12'}
                  >{tq`We’re still deciding`}</TextV2>
                }
                checked={blockWeddingDate}
                onChange={e => {
                  setBlockWeddingDate(e.target.checked);
                  telemetry.inputHandler('eventDate', 'onChange');
                }}
              />
            </SpacingStack>
          </SpacingStack>
          <SpacingStack
            spacing={7}
            marginTop={6}
          >
            <ButtonV2
              shape="rounded"
              loading={props.isCreateCtaLoading || formik.isSubmitting}
              type="submit"
              intent="newPrimary"
            >
              {tq`Create Event`}
            </ButtonV2>
            <Flex
              textAlign="center"
              justifyContent="center"
              flexWrap="wrap"
              typographyVariant="body2"
              alignItems="center"
              color="mono12"
              columnGap={1}
              minHeight={pxToRem(44)}
            >
              {isLoggedIn && user?.email && (
                <>
                  <TextV2
                    as="span"
                    wordBreak="break-word"
                  >{tq`Not ${user.email}?`}</TextV2>
                  <InlineLinkButton onClick={signOut}>{tq`Sign Out`}</InlineLinkButton>
                </>
              )}
            </Flex>
          </SpacingStack>
        </Box>
      </FormikProvider>
    </Flex>
  );
});

export default CreateWeddingForm;
